/**
 * Overview.jsx — Subtab I
 */

import React from "react";
import { cxTokens as t } from "../../../lib/coconsolexTokens.js";
import { PrivilegeBubble } from "../diagrams.jsx";
import { SubtabHeading, Body, P } from "../SubtabFrame.jsx";
import SynthesisBlock from "../SynthesisBlock.jsx";

const PILLARS = [
  {
    n: "1",
    title: "The Doctrinal Frame Is Co-Counsel",
    body:
      "CoConsoleX is itself a law firm. Its attorneys are licensed and bound by the Rules of Professional Conduct. Engagements are documented in lawyer-to-lawyer engagement letters, governed by Rule 1.5 fee reasonableness, and supervised under Rules 5.1 and 5.3. Privilege flows through both firms because both are providing legal services to the same client. There is no third-party disclosure occurring — and therefore no third-party doctrine question to litigate, no Kovel extension to argue.",
  },
  {
    n: "2",
    title: "The Infrastructure Is Air-Gapped",
    body:
      "Per-firm dedicated environments operate in physical isolation from the public internet, with concentric firewall architecture borrowed from the intelligence community's classified network design. Model updates and curated data flow inward through unidirectional gateways (data diodes from Owl Cyber Defense or Waterfall Security Solutions); nothing flows outward.",
  },
  {
    n: "3",
    title: "The Supervising Lawyer Is Real",
    body:
      "Every CoConsoleX engagement is supervised by a named attorney who exercises independent legal judgment over the AI-augmented work product, takes professional responsibility for output, and is bound by professional discipline for breaches in a way no vendor employee is. This is not a sysadmin who happens to have a JD. This is co-counsel, in the centuries-old sense of the term.",
  },
];

export default function Overview() {
  return (
    <article>
      <SubtabHeading roman="I">CoConsoleX from CaseSignalHQ</SubtabHeading>
      <p
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontStyle: "italic",
          fontSize: 22,
          color: t.slate,
          margin: "0 0 28px 0",
          lineHeight: 1.4,
        }}
      >
        A Confidential AI Co-Counsel Service for the Legal Profession.
      </p>

      <PrivilegeBubble />

      <Body>
        <h2
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 500,
            fontSize: 24,
            margin: "12px 0 14px",
          }}
        >
          What CoConsoleX Is
        </h2>
        <P>
          CoConsoleX is a confidential AI co-counsel service offered by
          CaseSignalHQ. It is structured as a lawyer-to-lawyer engagement —
          your firm engages CoConsoleX as co-counsel under the same doctrine
          that has governed multi-firm legal collaboration for over a century.
          The CoConsoleX attorney provides AI-augmented legal services
          (research, drafting, analysis, document review) using air-gapped
          infrastructure that your firm or client data never leaves.
        </P>
        <P>
          The architecture is deliberately the inverse of the prevailing legal
          AI market. Where Harvey, CoCounsel from Thomson Reuters, Lexis+ AI,
          and enterprise ChatGPT operate as cloud vendors with contractual
          confidentiality assurances, CoConsoleX operates as licensed legal
          counsel with structural confidentiality protection. Where existing
          tools require firms to trust a vendor's security promises about data
          that, if leaked, could end careers and trigger malpractice claims,
          CoConsoleX confidentiality is preserved by the engagement structure
          itself.
        </P>
      </Body>

      {/* Three pillars */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 20,
          margin: "40px 0 16px",
        }}
      >
        {PILLARS.map((p) => (
          <div
            key={p.n}
            style={{
              border: `1px solid ${t.rule}`,
              padding: "22px 22px 26px",
              background: t.paper,
            }}
          >
            <div
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontStyle: "italic",
                color: t.slate,
                fontSize: 13,
                letterSpacing: "0.2em",
                marginBottom: 8,
                textTransform: "uppercase",
              }}
            >
              Pillar {p.n}
            </div>
            <h3
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 22,
                margin: "0 0 12px 0",
                lineHeight: 1.2,
                fontWeight: 500,
              }}
            >
              {p.title}
            </h3>
            <p
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 14,
                lineHeight: 1.65,
                margin: 0,
                color: t.ink,
              }}
            >
              {p.body}
            </p>
          </div>
        ))}
      </div>

      <Body>
        <P>
          The result is a service that gives law firms, government agencies,
          judges, universities, and individual litigants what the current AI
          market does not offer: structurally confidential AI access,
          defensible under existing legal doctrine, deliverable today.
        </P>
      </Body>

      <SynthesisBlock />
    </article>
  );
}
